import { resolveMappedFiatCurrency } from '../constants/deposit-fiat-geo-map.js';
import { creditUsdToPayFiat, fiatToCreditUsd } from './toppay-amount.js';
import { TOPPAY_REGION_TO_CURRENCY, topPayAmountDecimals } from './toppay-region.js';
import { normalizeCountryCode } from './ip-country-lookup.js';

/** 无小数法币（伪汇率换算后取整） */
const ZERO_DECIMAL_FIATS = new Set([
  'VND',
  'JPY',
  'KRW',
  'IDR',
  'CLP',
  'HUF',
  'UGX',
  'KES',
  'TZS',
  'BIF',
  'DJF',
  'GNF',
  'ISK',
  'KMF',
  'PYG',
  'RWF',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

export function resolveFiatPayMin(fiatRow, fallback = 5) {
  const apiMin = Number(fiatRow?.payMin);
  if (Number.isFinite(apiMin) && apiMin > 0) return apiMin;
  const fb = Number(fallback);
  return Number.isFinite(fb) && fb > 0 ? fb : 5;
}

/** @param {Array<{ country?: string, currency?: string, payMin?: number }>} rows */
export function findUsdFiatRow(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return (
    list.find((row) => row.currency === 'USD' && row.country === 'US') ??
    list.find((row) => row.currency === 'USD') ??
    null
  );
}

function rowCountry(row) {
  return (
    normalizeCountryCode(row?.country) ||
    normalizeCountryCode(row?.countryName) ||
    String(row?.country ?? '')
      .trim()
      .toUpperCase()
  );
}

function rowCurrency(row) {
  return String(row?.currency ?? '')
    .trim()
    .toUpperCase();
}

/** USD is 1:1. Other currencies use rates map (TopPay channel first; later backend mid-market). */
function resolveFiatPerUsd(currency, rates) {
  const ccy = String(currency ?? '')
    .trim()
    .toUpperCase();
  if (ccy === 'USD') return 1;
  const n = Number(rates?.[ccy]);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** TopPay 原通道给了汇率：越/巴/墨。美、印未给，不进切币通道价。 */
const TOPPAY_CHANNEL_FX_REGIONS = new Set(['VN', 'BR', 'MX']);

/**
 * 切币汇率表：只用 VN/BR/MX 的通道价；USD=1。
 * INR/AED 等用公开中间价补，见 mergeCashFiatRates。
 */
export function buildCashFiatPerUsdByCurrency(toppayRegions) {
  const map = { USD: 1 };
  if (!toppayRegions || typeof toppayRegions !== 'object') return map;
  for (const [region, cfg] of Object.entries(toppayRegions)) {
    const regionCode = String(region || '').trim().toUpperCase();
    if (!TOPPAY_CHANNEL_FX_REGIONS.has(regionCode)) continue;
    if (!cfg?.enabled) continue;
    const ccy = String(cfg.currency || TOPPAY_REGION_TO_CURRENCY[regionCode] || '')
      .trim()
      .toUpperCase();
    const rate = Number(cfg.fiatPerUsd);
    if (!ccy || !Number.isFinite(rate) || rate <= 0) continue;
    map[ccy] = rate;
  }
  return map;
}

/** 通道价优先，公开中间价只补没有的币（不覆盖 VND/BRL/MXN）。 */
export function mergeCashFiatRates(channelRates, publicRates) {
  const map = { USD: 1 };
  if (channelRates && typeof channelRates === 'object') {
    for (const [ccy, rate] of Object.entries(channelRates)) {
      const code = String(ccy || '')
        .trim()
        .toUpperCase();
      const n = Number(rate);
      if (!code || !Number.isFinite(n) || n <= 0) continue;
      map[code] = n;
    }
  }
  map.USD = 1;
  if (publicRates && typeof publicRates === 'object') {
    for (const [ccy, rate] of Object.entries(publicRates)) {
      const code = String(ccy || '')
        .trim()
        .toUpperCase();
      if (!code || code === 'USD' || map[code]) continue;
      const n = Number(rate);
      if (!Number.isFinite(n) || n <= 0) continue;
      map[code] = n;
    }
  }
  return map;
}

function fiatAmountToCreditUsd(amount, fiat, rates) {
  const ccy = rowCurrency(fiat);
  const rate = resolveFiatPerUsd(ccy, rates);
  if (rate) return fiatToCreditUsd(amount, rate, topPayAmountDecimals(ccy));
  const n = Number(amount);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function creditUsdToFiatAmount(usd, fiat, rates) {
  const ccy = rowCurrency(fiat);
  const rate = resolveFiatPerUsd(ccy, rates);
  if (rate) return creditUsdToPayFiat(usd, rate, ccy);
  const u = Number(usd);
  return Number.isFinite(u) && u > 0 ? u : 0;
}

function minFiatForCreditUsd(fiat, rates, usMinFallback) {
  const ccy = rowCurrency(fiat);
  const rate = resolveFiatPerUsd(ccy, rates);
  if (rate) return creditUsdToPayFiat(usMinFallback, rate, ccy);
  return usMinFallback;
}

export function cashFiatUsesFxRate(currency, rates) {
  return resolveFiatPerUsd(currency, rates) > 0;
}

/**
 * 切币用的入账 USD。有通道汇率则从金额反推；无汇率时用上次记住的 USD（框内暂当美元等值）。
 */
export function resolveSwitchCreditUsd(amount, fromFiat, options = {}) {
  const usMinFallback = Number(options.usMinFallback) > 0 ? Number(options.usMinFallback) : 5;
  const rates =
    options.fiatPerUsdByCurrency && typeof options.fiatPerUsdByCurrency === 'object'
      ? options.fiatPerUsdByCurrency
      : {};
  const known = Number(options.knownCreditUsd);
  if (!cashFiatUsesFxRate(rowCurrency(fromFiat), rates) && known > 0) {
    return known;
  }
  return fiatAmountToCreditUsd(amount, fromFiat, rates);
}

function findRowByCountry(list, countryCode) {
  const code = normalizeCountryCode(countryCode);
  if (!code) return null;
  return list.find((row) => rowCountry(row) === code) ?? null;
}

/**
 * 出口 IP 国家 → 映射表默认币种 → 已支持列表。
 * 同币种多行时优先国家码一致；映射币种不在列表时再按国家行兜底；都没有 → USD。
 * @param {Array<{ country?: string, countryName?: string, currency?: string, key?: string }>} normalizedRows
 * @param {string | null | undefined} countryCode
 */
export function resolveDefaultFiatRow(normalizedRows, countryCode) {
  const list = Array.isArray(normalizedRows) ? normalizedRows : [];
  if (!list.length) return null;

  const code = normalizeCountryCode(countryCode);
  const mapped = resolveMappedFiatCurrency(code);
  if (mapped) {
    const byCountryAndCcy = list.find(
      (row) => rowCountry(row) === code && rowCurrency(row) === mapped
    );
    if (byCountryAndCcy) return byCountryAndCcy;
    const byCcy = list.find((row) => rowCurrency(row) === mapped);
    if (byCcy) return byCcy;
  }

  const byCountry = findRowByCountry(list, code);
  if (byCountry) return byCountry;

  return findUsdFiatRow(list);
}

export function formatFiatAmountValue(value, currency) {
  const code = String(currency ?? '')
    .trim()
    .toUpperCase();
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  if (ZERO_DECIMAL_FIATS.has(code)) {
    return String(Math.round(n));
  }
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

/**
 * 已输入金额低于 payMin 时抬到最小值；空输入保持空（不自动填）。
 * @param {string | number | null | undefined} amount
 * @param {number | null | undefined} min
 * @param {string | null | undefined} [currency]
 * @returns {string}
 */
export function clampFiatAmountToPayMin(amount, min, currency) {
  const raw = String(amount ?? '').trim();
  if (!raw) return '';

  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;

  const payMin = Number(min);
  if (!Number.isFinite(payMin) || payMin <= 0) {
    return formatFiatAmountValue(n, currency);
  }
  if (n < payMin) return formatFiatAmountValue(payMin, currency);
  return formatFiatAmountValue(n, currency);
}

/**
 * 切币：先换成入账 USD，再换成目标法币。
 * 切币：VN/BR/MX 用通道价，其余用公开中间价。USD=1。禁止 Alchemy payMin 当汇率。
 *
 * @param {number} amount
 * @param {{ payMin?: number, payMax?: number, currency?: string } | null} fromFiat
 * @param {{ payMin?: number, payMax?: number, currency?: string } | null} toFiat
 * @param {{ usMinFallback?: number, fiatPerUsdByCurrency?: Record<string, number>, knownCreditUsd?: number }} [options]
 * @returns {string}
 */
export function convertCashAmountByPseudoRate(amount, fromFiat, toFiat, options = {}) {
  const usMinFallback = Number(options.usMinFallback) > 0 ? Number(options.usMinFallback) : 5;
  const rates = options.fiatPerUsdByCurrency && typeof options.fiatPerUsdByCurrency === 'object'
    ? options.fiatPerUsdByCurrency
    : {};
  const currency = toFiat?.currency;
  const toMin = minFiatForCreditUsd(toFiat, rates, usMinFallback);

  const num = Number(amount);
  if (!Number.isFinite(num) || num <= 0 || !toMin) {
    return formatFiatAmountValue(toMin || usMinFallback, currency);
  }

  const usd = resolveSwitchCreditUsd(num, fromFiat, {
    usMinFallback,
    fiatPerUsdByCurrency: rates,
    knownCreditUsd: options.knownCreditUsd,
  });
  let next = usd > 0 ? creditUsdToFiatAmount(usd, toFiat, rates) : 0;
  if (!next) next = toMin;

  const toMax = Number(toFiat?.payMax);
  if (Number.isFinite(toMax) && toMax > 0) {
    next = Math.min(next, toMax);
  }
  next = Math.max(next, toMin);

  return formatFiatAmountValue(next, currency);
}

/**
 * 首次进入 / 无输入时：按 defaultUsd（默认 20 USD 等值）填入当前法币。
 *
 * @param {{ payMin?: number, payMax?: number, currency?: string } | null} fiatRow
 * @param {{
 *   defaultUsd?: number,
 *   usMinFallback?: number,
 *   usFiatRow?: object | null,
 *   fiatPerUsdByCurrency?: Record<string, number>,
 * }} [options]
 * @returns {string}
 */
export function resolveInitialCashAmountUsdBase(fiatRow, options = {}) {
  const defaultUsd = Number(options.defaultUsd) > 0 ? Number(options.defaultUsd) : 20;
  const usMinFallback = Number(options.usMinFallback) > 0 ? Number(options.usMinFallback) : 5;
  const usFiat = options.usFiatRow ??
    findUsdFiatRow(options.fiatRows) ?? {
      currency: 'USD',
      payMin: usMinFallback,
    };

  return convertCashAmountByPseudoRate(defaultUsd, usFiat, fiatRow, {
    usMinFallback,
    fiatPerUsdByCurrency: options.fiatPerUsdByCurrency,
  });
}
